/**
 * GroupLink One design tokens (CSS custom properties).
 *
 * The single source of truth for the BFF Apps redesign: the raw brand ramps, the semantic
 * aliases components actually reference, and the spacing / radius / shadow / typography
 * scales. Values come from the design handoff's token sheet
 * (`design_handoff_bff_apps/_ds/*\/tokens/`), which in turn mirrors the Figma variable
 * collection.
 *
 * Injected once by {@link DesignTokens}. Components must reference the SEMANTIC aliases
 * (`--color-surface`, `--space-ml`), never the raw ramps (`--gl-neutral-200`) — the ramps
 * are what the dark theme re-points, so reaching past the alias breaks dark mode.
 *
 * @module Styles
 */

import { css } from '@emotion/react';
import type { SerializedStyles } from '@emotion/react';

/** Raw brand palette + the light/dark semantic aliases and the layout scales. */
export const designTokens = (): SerializedStyles => css`
  :root {
    /* ---- Neutrals / base ---- */
    --gl-white: #ffffff;
    --gl-black: #000000;
    --gl-neutral-900: #313337;
    --gl-neutral-700: #58616a;
    --gl-neutral-500: #888e9e;
    --gl-neutral-300: #b2bdc5;
    --gl-neutral-200: #e2e8ef;
    --gl-neutral-100: #f5f8f9;

    /* ---- Primary: GL Blue ---- */
    --gl-blue: #0033ff;
    --gl-blue-dark: #002ee6;
    --gl-blue-90: #1a47ff;
    --gl-blue-75: #4063ff;
    --gl-blue-50: #7f98ff;
    --gl-blue-25: #bfccff;
    --gl-blue-10: #e6ebff;

    /* ---- Secondary ---- */
    --gl-navy: #201f5d;
    --gl-navy-dark: #1d1c54;
    --gl-navy-50: #908fae;
    --gl-navy-25: #c8c7d6;
    --gl-navy-10: #e9e9ef;

    --gl-blue-branding: #0066ff;
    --gl-blue-branding-50: #7fb3ff;
    --gl-blue-branding-25: #bfd9ff;
    --gl-blue-branding-10: #e6f0ff;

    --gl-old-blue: #0099ff;
    --gl-old-blue-50: #7fccff;
    --gl-old-blue-25: #bfe6ff;
    --gl-old-blue-10: #e6f5ff;

    /* ---- Accents ---- */
    --gl-purple: #7523d7;
    --gl-purple-dark: #6920c2;
    --gl-purple-50: #ba91eb;
    --gl-purple-25: #dcc8f5;
    --gl-purple-10: #f2e9fb;

    --gl-light-blue: #00dcff;
    --gl-light-blue-50: #7feeff;
    --gl-light-blue-25: #bff6ff;
    --gl-light-blue-10: #e6fbff;

    --gl-light-green: #00ffaa;
    --gl-light-green-50: #7fffd4;
    --gl-light-green-25: #bfffea;
    --gl-light-green-10: #e6fff6;

    --gl-orange: #ff925b;
    --gl-orange-50: #ffc8ad;
    --gl-orange-25: #ffe4d6;
    --gl-orange-10: #fff4ee;

    /* ---- Alerts ---- */
    --gl-green: #00893c;
    --gl-green-dark: #007b36;
    --gl-green-50: #7fc49d;
    --gl-green-25: #bfe1ce;
    --gl-green-10: #e6f3ec;

    --gl-yellow: #e4a700;
    --gl-yellow-dark: #cd9600;
    --gl-yellow-50: #f2d37f;
    --gl-yellow-25: #f8e9bf;
    --gl-yellow-10: #fcf6e6;

    --gl-red: #e12c57;
    --gl-red-dark: #cb274e;
    --gl-red-50: #f095ab;
    --gl-red-25: #f7cad5;
    --gl-red-10: #fceaef;

    /* ---- Spacing scale ---- */
    --space-xxs: 2px;
    --space-xs: 4px;
    --space-s: 6px;
    --space-sm: 10px;
    --space-m: 14px;
    --space-base: 16px;
    --space-ml: 20px;
    --space-l: 28px;
    --space-xl: 40px;
    --space-2xl: 56px;
    --space-3xl: 80px;

    /* ---- Radius scale ---- */
    --radius-xs: 2px;
    --radius-s: 4px;
    --radius-sm: 6px;
    --radius-m: 10px;
    --radius-ml: 16px;
    --radius-lg: 24px;
    --radius-xl: 36px;
    --radius-round: 999px;
    --radius-control: var(--radius-m);
    --radius-card: var(--radius-ml);
    --radius-pill: var(--radius-round);

    /* ---- Elevation ---- */
    --shadow-xs: 0 1px 2px rgba(106, 147, 184, 0.12);
    --shadow-sm: 0 2px 6px rgba(106, 147, 184, 0.14);
    --shadow-md: 0 6px 16px rgba(106, 147, 184, 0.16);
    --shadow-lg: 0 12px 32px rgba(32, 31, 93, 0.16);
    --shadow-focus: 0 0 0 3px rgba(0, 51, 255, 0.25);

    /* ---- Borders ---- */
    --border-width: 1px;
    --border-width-strong: 1.5px;

    /* ---- Layout ---- */
    --layout-sidebar-width: 248px;
    --layout-sidebar-collapsed: 72px;
    --layout-header-height: 64px;
    --layout-content-max: 1440px;
    --layout-gutter: 24px;

    /* ---- Motion ---- */
    --ease-standard: cubic-bezier(0.2, 0, 0, 1);
    --ease-emphasized: cubic-bezier(0.3, 0, 0, 1);
    --ease-out: cubic-bezier(0, 0, 0.2, 1);
    --duration-fast: 120ms;
    --duration-base: 200ms;
    --duration-slow: 320ms;

    /* ---- z-index ---- */
    --z-base: 1;
    --z-sticky: 100;
    --z-drawer: 200;
    --z-overlay: 900;
    --z-modal: 1000;
    --z-toast: 1100;
    --z-tooltip: 1200;

    /* ---- Typography ---- */
    --font-sans: 'Figtree', -apple-system, 'Segoe UI', system-ui, sans-serif;
    --font-mono: 'Consolas', 'SF Mono', ui-monospace, monospace;
    --font-weight-regular: 400;
    --font-weight-medium: 500;
    --font-weight-semibold: 600;
    --font-weight-bold: 700;

    /* size scale */
    --font-size-2xs: 11px;
    --font-size-xs: 12px;
    --font-size-sm: 13px;
    --font-size-md: 16px;
    --font-size-lg: 19px;
    --font-size-xl: 23px;
    --font-size-2xl: 28px;
    --font-size-3xl: 36px;
    --font-size-4xl: 48px;

    /* line heights */
    --line-height-tight: 1.2;
    --line-height-snug: 1.35;
    --line-height-normal: 1.5;
    --line-height-relaxed: 1.65;

    /* letter spacing */
    --letter-spacing-tight: -0.01em;
    --letter-spacing-normal: 0;
    --letter-spacing-wide: 0.02em;
    --letter-spacing-caps: 0.06em;
  }

  /* ---- Semantic aliases: light (default) ---- */
  :root,
  [data-theme='light'] {
    --color-bg: var(--gl-neutral-100);
    --color-surface: var(--gl-white);
    --color-surface-raised: var(--gl-white);
    --color-surface-sunken: var(--gl-neutral-100);
    --color-surface-hover: var(--gl-neutral-100);
    --color-surface-selected: var(--gl-blue-10);
    --color-overlay: rgba(32, 31, 93, 0.45);

    --color-border: var(--gl-neutral-200);
    --color-border-strong: var(--gl-neutral-300);
    --color-divider: var(--gl-neutral-200);

    --color-text: var(--gl-neutral-900);
    --color-text-secondary: var(--gl-neutral-700);
    --color-text-muted: var(--gl-neutral-500);
    --color-text-disabled: var(--gl-neutral-300);
    --color-text-inverse: var(--gl-white);
    --color-text-link: var(--gl-blue);

    --color-primary: var(--gl-blue);
    --color-primary-hover: var(--gl-blue-dark);
    --color-primary-active: var(--gl-navy);
    --color-on-primary: var(--gl-white);
    --color-primary-subtle: var(--gl-blue-10);
    --color-primary-subtle-text: var(--gl-blue);

    --color-focus-ring: var(--gl-blue-50);

    --color-success: var(--gl-green);
    --color-success-subtle: var(--gl-green-10);
    --color-warning: var(--gl-yellow);
    --color-warning-subtle: var(--gl-yellow-10);
    --color-danger: var(--gl-red);
    --color-danger-subtle: var(--gl-red-10);
    --color-info: var(--gl-old-blue);
    --color-info-subtle: var(--gl-old-blue-10);

    --color-water: var(--gl-old-blue);
    --color-gas: var(--gl-orange);
    --color-energy: var(--gl-yellow);
    --color-streetlight: var(--gl-purple);
  }

  /* ---- Semantic aliases: dark ---- */
  [data-theme='dark'] {
    --color-bg: #131417;
    --color-surface: #1d1f24;
    --color-surface-raised: #24272d;
    --color-surface-sunken: #0f1013;
    --color-surface-hover: #24272d;
    --color-surface-selected: rgba(0, 51, 255, 0.18);
    --color-overlay: rgba(0, 0, 0, 0.6);

    --color-border: #2d3036;
    --color-border-strong: #3c404a;
    --color-divider: #2d3036;

    --color-text: #eef1f5;
    --color-text-secondary: #b2bdc5;
    --color-text-muted: #888e9e;
    --color-text-disabled: #58616a;
    --color-text-inverse: #131417;
    --color-text-link: var(--gl-old-blue);

    --color-primary: var(--gl-blue);
    --color-primary-hover: #1a47ff;
    --color-primary-active: var(--gl-old-blue);
    --color-on-primary: var(--gl-white);
    --color-primary-subtle: rgba(0, 51, 255, 0.16);
    --color-primary-subtle-text: var(--gl-blue-50);

    --color-focus-ring: var(--gl-blue-75);

    --color-success: var(--gl-light-green);
    --color-success-subtle: rgba(0, 255, 170, 0.12);
    --color-warning: var(--gl-yellow);
    --color-warning-subtle: rgba(228, 167, 0, 0.14);
    --color-danger: #ff5a7e;
    --color-danger-subtle: rgba(225, 44, 87, 0.16);
    --color-info: var(--gl-old-blue);
    --color-info-subtle: rgba(0, 153, 255, 0.14);

    --color-water: var(--gl-old-blue);
    --color-gas: var(--gl-orange);
    --color-energy: var(--gl-yellow);
    --color-streetlight: var(--gl-purple-50);
  }
`;
